import { FormEvent, useCallback, useMemo, useState } from 'react';
import { toast } from '@/ui/toast';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { BrandGroupEntity } from '@/entities/brand-group.entity';
import {
    normalizeMergedGroupNameDraft,
    prepareGroupMergeSelection,
} from '@/components/action-buttons/group-merge.helpers';

export type GroupMergePayload = {
    groupIds: string[];
    name: string;
};

type GroupMergeTargetType = 'products' | 'brands';

type UseGroupMergeModalOrchestrationArgs = {
    pickedProducts: string[];
    pickedBrandGroups: string[];
    products: ProductGroupEntity[];
    brandGroups: BrandGroupEntity[];
    onMergeProducts: (payload: GroupMergePayload) => Promise<boolean>;
    onMergeBrands: (payload: GroupMergePayload) => Promise<boolean>;
};

type GroupMergeRequest = {
    type: GroupMergeTargetType;
    groupIds: string[];
};

type UseGroupMergeModalOrchestrationReturn = {
    isGroupMergeModalOpen: boolean;
    groupMergeNameDraft: string;
    setGroupMergeNameDraft: (value: string) => void;
    groupMergeTargetType: GroupMergeTargetType | null;
    onGroupPickedProducts: () => void;
    onGroupPickedBrands: () => void;
    closeGroupMergeModal: () => void;
    submitGroupMergeForm: (event?: FormEvent<HTMLFormElement>) => Promise<void>;
    isGroupMergeSubmitDisabled: boolean;
};

export const useGroupMergeModalOrchestration = ({
    pickedProducts,
    pickedBrandGroups,
    products,
    brandGroups,
    onMergeProducts,
    onMergeBrands,
}: UseGroupMergeModalOrchestrationArgs): UseGroupMergeModalOrchestrationReturn => {
    const [isGroupMergeModalOpen, setIsGroupMergeModalOpen] = useState(false);
    const [groupMergeNameDraft, setGroupMergeNameDraft] = useState('');
    const [groupMergeRequest, setGroupMergeRequest] = useState<GroupMergeRequest | null>(null);

    const closeGroupMergeModal = useCallback(() => {
        setIsGroupMergeModalOpen(false);
        setGroupMergeRequest(null);
        setGroupMergeNameDraft('');
    }, []);

    const openGroupMergeModal = useCallback(
        (
            targetType: GroupMergeTargetType,
            pickedGroupIds: string[],
            groups: Array<{
                id: string;
                name: string;
            }>,
        ) => {
            const selection = prepareGroupMergeSelection(pickedGroupIds, groups);
            if (!selection) {
                toast.warning('Выберите минимум 2 группы');
                return;
            }

            setGroupMergeRequest({
                type: targetType,
                groupIds: selection.groupIds,
            });
            setGroupMergeNameDraft(selection.defaultGroupName);
            setIsGroupMergeModalOpen(true);
        },
        [],
    );

    const onGroupPickedProducts = useCallback(() => {
        openGroupMergeModal('products', pickedProducts, products);
    }, [openGroupMergeModal, pickedProducts, products]);

    const onGroupPickedBrands = useCallback(() => {
        openGroupMergeModal('brands', pickedBrandGroups, brandGroups);
    }, [brandGroups, openGroupMergeModal, pickedBrandGroups]);

    const submitGroupMergeForm = useCallback(
        async (event?: FormEvent<HTMLFormElement>) => {
            event?.preventDefault();

            if (!groupMergeRequest) {
                return;
            }

            const normalizedGroupName = normalizeMergedGroupNameDraft(groupMergeNameDraft);
            if (!normalizedGroupName) {
                return;
            }

            const mergeAction = groupMergeRequest.type === 'products' ? onMergeProducts : onMergeBrands;
            const hasMerged = await mergeAction({
                groupIds: groupMergeRequest.groupIds,
                name: normalizedGroupName,
            });

            if (hasMerged) {
                closeGroupMergeModal();
            }
        },
        [closeGroupMergeModal, groupMergeNameDraft, groupMergeRequest, onMergeBrands, onMergeProducts],
    );

    const isGroupMergeSubmitDisabled = useMemo(
        () => !groupMergeRequest || !groupMergeNameDraft.trim(),
        [groupMergeNameDraft, groupMergeRequest],
    );

    return {
        isGroupMergeModalOpen,
        groupMergeNameDraft,
        setGroupMergeNameDraft,
        groupMergeTargetType: groupMergeRequest?.type || null,
        onGroupPickedProducts,
        onGroupPickedBrands,
        closeGroupMergeModal,
        submitGroupMergeForm,
        isGroupMergeSubmitDisabled,
    };
};
